import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { User } from '../types/user'
import { apiClient, API_GATEWAY_URL } from '../api/client'
import axios from 'axios'

interface AuthState {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  loginWithGoogle: (code: string, redirectUri: string) => Promise<void>
  logout: () => Promise<void>
  initialize: () => Promise<void>
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,

      loginWithGoogle: async (code: string, redirectUri: string) => {
        set({ isLoading: true })
        try {
          const response = await apiClient.post('/auth/google', { code, redirectUri })
          const { user, accessToken, refreshToken } = response.data.data
          
          localStorage.setItem('accessToken', accessToken)
          localStorage.setItem('refreshToken', refreshToken)
          
          set({
            user: {
              userId: user.userId,
              googleEmail: user.googleEmail,
              email: user.email,
              isAdmin: user.isAdmin,
            },
            isAuthenticated: true,
            isLoading: false,
          })
        } catch (error) {
          set({ isLoading: false })
          throw error
        }
      },

      logout: async () => {
        try {
          await apiClient.post('/auth/logout')
        } catch (error) {
          // Ignore logout errors
        }
        localStorage.removeItem('accessToken')
        localStorage.removeItem('refreshToken')
        set({ user: null, isAuthenticated: false })
      },

      initialize: async () => {
        const token = localStorage.getItem('accessToken')
        if (token) {
          try {
            const payload = JSON.parse(atob(token.split('.')[1]))
            const now = Math.floor(Date.now() / 1000)
            if (payload.exp >= now) {
              set({
                user: {
                  userId: payload.sub,
                  googleEmail: payload.username,
                  email: payload.username,
                  isAdmin: payload.isAdmin,
                },
                isAuthenticated: true,
              })
              return
            }
          } catch {
            // Token is malformed, try refresh below
          }
        }

        // Access token missing or expired — try refresh
        const refreshToken = localStorage.getItem('refreshToken')
        if (refreshToken) {
          try {
            const response = await axios.post(`${API_GATEWAY_URL}/api/auth/refresh`, {
              refreshToken,
            })
            const { accessToken, refreshToken: newRefreshToken } = response.data.data
            localStorage.setItem('accessToken', accessToken)
            if (newRefreshToken) {
              localStorage.setItem('refreshToken', newRefreshToken)
            }

            const payload = JSON.parse(atob(accessToken.split('.')[1]))
            set({
              user: {
                userId: payload.sub,
                googleEmail: payload.username,
                email: payload.username,
                isAdmin: payload.isAdmin,
              },
              isAuthenticated: true,
            })
            return
          } catch {
            // Refresh failed, clear tokens below
          }
        }

        localStorage.removeItem('accessToken')
        localStorage.removeItem('refreshToken')
        set({ user: null, isAuthenticated: false })
      },
    }),
    {
      name: 'auth-storage',
    }
  )
)
